import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks
 * Allows safe HTML tags while removing dangerous scripts and attributes
 * @param {string} html - The HTML string to sanitize
 * @returns {string} - Sanitized HTML safe for rendering
 */
export const sanitizeHTML = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Configure DOMPurify to allow common formatting tags
  const config = {
    ALLOWED_TAGS: [
      'b', 'i', 'u', 'strong', 'em', 'p', 'br', 'span', 'div',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'img',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'blockquote', 'pre', 'code'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'style',
      'width', 'height', 'target', 'rel'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
  };

  return DOMPurify.sanitize(html, config);
};

/**
 * Sanitizes plain text by escaping HTML entities
 * Use this for user input that should be displayed as text only
 * @param {string} text - The text to sanitize
 * @returns {string} - Escaped text safe for rendering
 */
export const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * Sanitizes email content with strict rules for email campaigns
 * Allows basic formatting but removes potentially dangerous content
 * @param {string} emailContent - The email HTML to sanitize
 * @returns {string} - Sanitized email HTML
 */
export const sanitizeEmailContent = (emailContent) => {
  if (!emailContent || typeof emailContent !== 'string') {
    return '';
  }

  // More restrictive config for email content
  const config = {
    ALLOWED_TAGS: [
      'b', 'i', 'u', 'strong', 'em', 'p', 'br', 'span', 'div',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'img',
      'table', 'thead', 'tbody', 'tr', 'th', 'td'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'style',
      'width', 'height', 'align'
    ],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover']
  };

  return DOMPurify.sanitize(emailContent, config);
};

/**
 * Sanitizes lead/contact data that may contain user-generated content
 * @param {string} content - The content to sanitize
 * @returns {string} - Sanitized content
 */
export const sanitizeLeadContent = (content) => {
  if (!content || typeof content !== 'string') {
    return '';
  }

  // Very restrictive - only allow basic text formatting
  const config = {
    ALLOWED_TAGS: ['b', 'i', 'strong', 'em', 'br'],
    ALLOWED_ATTR: []
  };

  return DOMPurify.sanitize(content, config);
};
